
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Plus, Search, Mail, Edit, Trash2, CheckCircle, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface StaffMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
}

interface StaffFormValues {
  full_name: string;
  email: string;
  role: string;
}

export function StaffManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const queryClient = useQueryClient();

  const staffForm = useForm<StaffFormValues>({
    defaultValues: {
      full_name: "",
      email: "",
      role: "staff",
    },
  });

  // Effet pour remplir le formulaire lors de l'édition
  useEffect(() => {
    if (editingStaff) {
      staffForm.reset({
        full_name: editingStaff.full_name || "",
        email: editingStaff.email,
        role: editingStaff.role,
      });
    } else {
      staffForm.reset({
        full_name: "",
        email: "",
        role: "staff",
      });
    }
  }, [editingStaff, staffForm]);

  // Récupération des membres du personnel
  const { data: staffMembers, isLoading } = useQuery({
    queryKey: ["staff-members"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("staff_members")
        .select("*")
        .eq("professional_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Mutation pour ajouter un membre du personnel
  const addStaffMutation = useMutation({
    mutationFn: async (values: StaffFormValues) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("staff_members")
        .insert({
          professional_id: user.id,
          full_name: values.full_name,
          email: values.email,
          role: values.role,
          status: "pending",
        })
        .select("*")
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] });
      setIsAddingStaff(false);
      staffForm.reset();
      toast.success("Membre du personnel ajouté avec succès");
    },
    onError: (error: any) => {
      console.error("Error adding staff member:", error);
      toast.error("Erreur lors de l'ajout du membre du personnel");
    },
  });

  // Mutation pour mettre à jour un membre du personnel
  const updateStaffMutation = useMutation({
    mutationFn: async (values: StaffFormValues & { id: string }) => {
      const { data, error } = await supabase
        .from("staff_members")
        .update({
          full_name: values.full_name,
          email: values.email,
          role: values.role,
        })
        .eq("id", values.id)
        .select("*")
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] });
      setEditingStaff(null);
      staffForm.reset();
      toast.success("Membre du personnel mis à jour avec succès");
    },
    onError: (error: any) => {
      console.error("Error updating staff member:", error);
      toast.error("Erreur lors de la mise à jour du membre du personnel");
    },
  });

  // Mutation pour supprimer un membre du personnel
  const deleteStaffMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("staff_members")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] });
      toast.success("Membre du personnel supprimé avec succès");
    },
    onError: (error: any) => {
      console.error("Error deleting staff member:", error);
      toast.error("Erreur lors de la suppression du membre du personnel");
    },
  });

  // Mutation pour mettre à jour le statut d'un membre du personnel
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await supabase
        .from("staff_members")
        .update({ status })
        .eq("id", id)
        .select("*")
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] });
      toast.success("Statut mis à jour avec succès");
    },
    onError: (error: any) => {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    },
  });

  // Envoyer le formulaire
  const onSubmit = (values: StaffFormValues) => {
    if (editingStaff) {
      updateStaffMutation.mutate({ ...values, id: editingStaff.id });
    } else {
      addStaffMutation.mutate(values);
    }
  };

  // Filtrage des membres du personnel
  const filteredStaffMembers = staffMembers?.filter((member) =>
    member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl font-semibold">Gestion du personnel</h2>
        <Dialog 
          open={isAddingStaff || editingStaff !== null} 
          onOpenChange={(open) => {
            setIsAddingStaff(open);
            if (!open) setEditingStaff(null);
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={() => setIsAddingStaff(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un membre
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingStaff ? "Modifier un membre" : "Ajouter un membre"}
              </DialogTitle>
            </DialogHeader>
            <Form {...staffForm}>
              <form onSubmit={staffForm.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={staffForm.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom complet</FormLabel>
                      <FormControl>
                        <Input placeholder="Nom complet" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={staffForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="email@example.com" 
                          {...field} 
                          disabled={!!editingStaff}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={staffForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rôle</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="staff">Personnel</SelectItem>
                          <SelectItem value="assistant">Assistant</SelectItem>
                          <SelectItem value="nurse">Infirmier(e)</SelectItem>
                          <SelectItem value="doctor">Médecin</SelectItem>
                          <SelectItem value="admin">Administrateur</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setIsAddingStaff(false);
                      setEditingStaff(null);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={addStaffMutation.isPending || updateStaffMutation.isPending}
                  >
                    {(addStaffMutation.isPending || updateStaffMutation.isPending) && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    {editingStaff ? "Mettre à jour" : "Ajouter"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par nom, email ou rôle..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <ScrollArea className="h-[calc(100vh-250px)]">
        <div className="space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-5 w-1/3 bg-muted rounded mb-2"></div>
                  <div className="h-4 w-1/4 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))
          ) : filteredStaffMembers?.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                {searchQuery ? (
                  <p>Aucun membre ne correspond à votre recherche.</p>
                ) : (
                  <>
                    <p className="mb-4">Aucun membre du personnel n'a été trouvé.</p>
                    <Button onClick={() => setIsAddingStaff(true)}>
                      Ajouter un membre
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredStaffMembers?.map((staff) => (
              <Card key={staff.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">{staff.full_name || staff.email}</h3>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Mail className="h-3.5 w-3.5 mr-1" />
                        {staff.email}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="capitalize">
                          {staff.role}
                        </Badge>
                        <Badge
                          variant={
                            staff.status === "active"
                              ? "default"
                              : staff.status === "pending"
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {staff.status === "active"
                            ? "Actif"
                            : staff.status === "pending"
                            ? "En attente"
                            : "Rejeté"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {staff.status === "pending" && (
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-green-600 hover:text-green-700 hover:bg-green-100"
                            onClick={() => updateStatusMutation.mutate({ id: staff.id, status: "active" })}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-100"
                            onClick={() => updateStatusMutation.mutate({ id: staff.id, status: "rejected" })}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingStaff(staff)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-100"
                        onClick={() => deleteStaffMutation.mutate(staff.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
